import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AudioPlayer } from '../components/audio/AudioPlayer'
import { VoiceOrb } from '../components/audio/VoiceOrb'
import { usePretext, useContainerWidth } from '../hooks/usePretext'

interface AudiobookChapter {
  title: string
  audioFile: string
  duration?: number
}

interface Audiobook {
  slug: string
  title: string
  chapters: AudiobookChapter[]
  totalDuration?: number
  outputDir: string
  createdAt?: string
}

interface AudiobookListResponse {
  audiobooks: Audiobook[]
  count: number
}

interface BookCardProps {
  book: Audiobook
  isSelected: boolean
  onSelect: () => void
}

function BookCard({ book, isSelected, onSelect }: BookCardProps) {
  const [cardRef, cardWidth] = useContainerWidth<HTMLButtonElement>()
  const { height: titleHeight } = usePretext(book.title, cardWidth, {
    font: '14px ui-sans-serif, system-ui, -apple-system, sans-serif',
    lineHeight: 20,
  })

  return (
    <button
      ref={cardRef}
      onClick={onSelect}
      className={`text-left p-4 rounded-lg border transition-colors ${
        isSelected
          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-400'
          : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-600'
      }`}
    >
      <p
        className="font-medium text-gray-900 dark:text-gray-100 text-sm leading-snug"
        style={{ minHeight: titleHeight ?? undefined }}
      >
        {book.title}
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {book.chapters.length} chapter{book.chapters.length !== 1 ? 's' : ''}
      </p>
    </button>
  )
}

export function AudiobookPage() {
  const [selected, setSelected] = useState<Audiobook | null>(null)
  const [activeChapter, setActiveChapter] = useState<number>(0)
  const [orbActive, setOrbActive] = useState(false)

  const { data, isLoading, error } = useQuery<AudiobookListResponse>({
    queryKey: ['audiobooks'],
    queryFn: () => fetch('http://localhost:3003/api/audiobook/list').then(r => r.json()),
  })

  const audiobooks = data?.audiobooks ?? []

  const [detailRef, detailWidth] = useContainerWidth<HTMLDivElement>()
  const { height: titleHeight } = usePretext(selected?.title ?? '', detailWidth, {
    font: '1.125rem ui-sans-serif, system-ui, -apple-system, sans-serif',
    lineHeight: 28,
  })

  const selectedChapter = selected?.chapters[activeChapter]
  const audioSrc = selectedChapter?.audioFile
    ? `http://localhost:3003/api/audiobook/${selected!.slug}/audio/${selectedChapter.audioFile}`
    : undefined

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <VoiceOrb active={orbActive} size={40} />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Audiobooks</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {audiobooks.length} audiobook{audiobooks.length !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>

      {isLoading && (
        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading audiobooks…</p>
      )}

      {error && (
        <p className="text-red-500 text-sm">Failed to load audiobooks. Is the API running?</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {audiobooks.map((book) => (
          <BookCard
            key={book.slug}
            book={book}
            isSelected={selected?.slug === book.slug}
            onSelect={() => { setSelected(book); setActiveChapter(0) }}
          />
        ))}
      </div>

      {selected && (
        <div ref={detailRef} className="border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
          <h2
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
            style={{ minHeight: titleHeight ?? undefined }}
          >
            {selected.title}
          </h2>

          <div className="flex gap-3 flex-wrap">
            {selected.chapters.map((ch, i) => (
              <button
                key={i}
                onClick={() => setActiveChapter(i)}
                className={`px-3 py-1.5 rounded text-xs transition-colors ${
                  activeChapter === i
                    ? 'bg-purple-600 text-white dark:bg-purple-500'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                }`}
              >
                {ch.title || `Chapter ${i + 1}`}
              </button>
            ))}
          </div>

          {audioSrc ? (
            <AudioPlayer
              src={audioSrc}
              title={selectedChapter?.title}
              className="mt-2"
              onPlay={() => setOrbActive(true)}
              onPause={() => setOrbActive(false)}
              onEnded={() => setOrbActive(false)}
            />
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No audio file for this chapter.
            </p>
          )}
        </div>
      )}

      {audiobooks.length === 0 && !isLoading && (
        <div className="text-center py-16 text-gray-400 dark:text-gray-600">
          <p className="text-sm">No audiobooks generated yet.</p>
          <p className="text-xs mt-1">Run <code className="font-mono">/audiobook-generate</code> to create one.</p>
        </div>
      )}
    </div>
  )
}
